import { Fragment, useMemo, useState } from 'react';
import type { ApiEndpoints, PmaDerEntityStatus } from '@avisoma/shared';
import { FaFile } from 'react-icons/fa6';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { ExtractionTableCard } from '../../components/reconstructedDocument/extractionTables/ExtractionTableCard';
import { RowInspectorDialog } from '../../components/reconstructedDocument/extractionTables/RowInspectorDialog';
import {
  PmaDerDeclarationsTable,
  PmaDerPartsTable,
  presentPmaDerScalarEntries,
} from '../../components/reconstructedDocument/extractionTables/PmaDerTables';
import { PeekLink } from '../../components/PeekLink';
import { ID } from '../../components/ID';
import { PageChips } from './PageChips';

interface PmaDerEventsProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

export function PmaDerEvents({ response }: PmaDerEventsProps) {
  const rawEvents = response.item.data?.pmaDerEvents?.pmaDerEvents;
  const [inspected, setInspected] = useState<unknown>(null);

  const events = useMemo(
    () =>
      (rawEvents ?? []).map((event) => ({
        ...event,
        pageIds: event.pageIds ?? [],
        declarations: event.declarations ?? [],
        parts: event.parts ?? [],
      })),
    [rawEvents],
  );

  const pageIdToPosition = useMemo(() => {
    const map = new Map<string, number>();
    events.forEach((event) => {
      event.pageIds.forEach((pageId, index) => {
        if (!map.has(pageId)) map.set(pageId, index + 1);
      });
    });
    return map;
  }, [events]);

  if (events.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card
        header={
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">PMA/DER Status</h2>
        }
      >
        <DescriptionList>
          <DescriptionTerm>Statement Count</DescriptionTerm>
          <DescriptionDetails>{events.length}</DescriptionDetails>
        </DescriptionList>
      </Card>

      {events.map((event, index) => (
        <PmaDerEntityCard
          key={`${event.reconstructedDocumentId}-${index}`}
          event={event}
          pageIdToPosition={pageIdToPosition}
          onInspect={setInspected}
        />
      ))}

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title="PMA/DER row"
        data={inspected}
      />
    </div>
  );
}

/**
 * The aircraft a NIS clearance letter is about (DR-6009). Only an aircraft-level letter's item
 * carries it; the consumer cuts its window to the engine's stays.
 */
function presentAircraftEntries(event: PmaDerEntityStatus): ReadonlyArray<readonly [string, string]> {
  const aircraft = [event.aircraftRegistration, event.aircraftMsn && `MSN ${event.aircraftMsn}`]
    .filter(Boolean)
    .join(' · ');
  return aircraft ? [['Aircraft', aircraft]] : [];
}

function PmaDerEntityCard({
  event,
  pageIdToPosition,
  onInspect,
}: {
  event: PmaDerEntityStatus & { pageIds: string[] };
  pageIdToPosition: Map<string, number>;
  onInspect: (row: unknown) => void;
}) {
  const scalars = [...presentPmaDerScalarEntries(event), ...presentAircraftEntries(event)];
  const declarationRows = event.declarations;
  const partRows = event.parts;

  return (
    <div className="space-y-4">
      <Card
        header={
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">
            {event.statementDate ? `PMA/DER Status · ${event.statementDate}` : 'PMA/DER Status'}
          </h2>
        }
      >
        <DescriptionList>
          {scalars.map(([label, value]) => (
            <Fragment key={label}>
              <DescriptionTerm>{label}</DescriptionTerm>
              <DescriptionDetails className={label === 'ESNs' ? 'font-mono' : undefined}>
                {value}
              </DescriptionDetails>
            </Fragment>
          ))}
          <DescriptionTerm>Declarations</DescriptionTerm>
          <DescriptionDetails>{declarationRows.length}</DescriptionDetails>
          <DescriptionTerm>Parts</DescriptionTerm>
          <DescriptionDetails>{partRows.length}</DescriptionDetails>
          {event.pageIds.length > 0 && (
            <>
              <DescriptionTerm>Source Pages</DescriptionTerm>
              <DescriptionDetails>
                <PageChips pageIds={event.pageIds} />
              </DescriptionDetails>
            </>
          )}
          {(event.reconstructedDocumentId || event.documentId) && (
            <>
              <DescriptionTerm>Document</DescriptionTerm>
              <DescriptionDetails>
                <div className="flex flex-col gap-0.5">
                  {event.reconstructedDocumentId && (
                    <PeekLink
                      type="reconstructed"
                      id={event.reconstructedDocumentId}
                      className="inline-flex items-center gap-1"
                    >
                      <FaFile className="size-3" />
                      <ID id={event.reconstructedDocumentId} />
                    </PeekLink>
                  )}
                  {event.documentId && (
                    <PeekLink
                      type="document"
                      id={event.documentId}
                      className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-zinc-400"
                    >
                      <FaFile className="size-3" />
                      <ID id={event.documentId} />
                    </PeekLink>
                  )}
                </div>
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      </Card>

      {declarationRows.length > 0 && (
        <ExtractionTableCard
          label="PMA/DER Declarations"
          rows={declarationRows}
          csvTitle="PMA-DER Declarations"
          bodyHeight="project"
        >
          <PmaDerDeclarationsTable
            rows={declarationRows}
            pageIdToPosition={pageIdToPosition}
            onInspect={onInspect}
          />
        </ExtractionTableCard>
      )}

      {partRows.length > 0 && (
        <ExtractionTableCard
          label="PMA/DER Parts"
          rows={partRows}
          csvTitle="PMA-DER Parts"
          bodyHeight="project"
        >
          <PmaDerPartsTable rows={partRows} pageIdToPosition={pageIdToPosition} onInspect={onInspect} />
        </ExtractionTableCard>
      )}
    </div>
  );
}
